/**
 * Number Kingdom, surveyed — the village and the inside of all five regions
 * (docs/rpg.md §22, `.claude/skills/world-maps/SKILL.md`).
 *
 * **The geography is the number line stood on end.** Place value is the flat
 * country in the middle, and everything else in this kingdom is a direction the
 * digits carry on in from there: east to the harbour, where they carry on past
 * the point; south under the rock, where they carry on under zero; and north up
 * the ridge, which is only climbed by powers. The foundry is the one place that
 * is not a direction — it is where a number already on the plain gets taken
 * apart, so it sits behind the village rather than out at an edge. A student
 * who walks out of the village four different ways has been told, by the walk,
 * that the number system grows in more than one.
 *
 * ```
 *   .  meadow / floor    #  lane        ~  water      "  field
 *   f  trees             =  pier        ^  rock       %  wall     ,  green / shore
 * ```
 *
 * **A place is a reference and never a second name.** Every region, the boss
 * and every concept here carry an id and nothing else; the words on their
 * plates are read off the kingdom and the curriculum, so a plate and the card
 * under the map cannot disagree. Only the notice board and the ways out — which
 * name nothing in the graph — carry their own.
 *
 * **Terrain is scenery and never a lock.** The sea, the rock and the foundry
 * wall stop a step and never a region, a concept or a lesson. `validateTown`
 * and `validateRegionMap` walk all six of these from their arrival tiles on
 * every `npm run content:validate`; every place below is also an ordinary card
 * or link on the kingdom page, so losing the map costs a student nothing but
 * the pleasure of it.
 */

import type { RegionMap, Town } from '@yuan-math/world';

const NUMBER = 'kingdom-number';

/* -------------------------------------------------------------------------- */
/* The village                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The high road runs west to east across the plains, and every other way out
 * of this kingdom leaves it at a right angle.
 *
 * North of the road the fields are marked out in columns, and each column west
 * of the last is the bigger field — the only true thing a fence can say about a
 * ten times. A lane climbs out of them into the mountains to Root and Power
 * Ridge. East the road runs down to the water and stops there, and the pier
 * carries on where the road cannot, out to Decimal Docks. South a shaft drops
 * through the rock to Integer Caverns, below the level everything else stands
 * on. The Prime Foundry is the walled works behind the village, off the road
 * and inside a gate, because taking a number apart is not a journey anywhere.
 * The gate on the headland is Serla's opposite number here, and a seal over
 * this kingdom would hold back her ceremony and not one tile of this map.
 */
const NUMBER_VILLAGE: Town = {
  kingdomId: NUMBER,
  caption:
    'The plains in the middle, the harbour east where the digits carry on past the point, the caverns under the rock where they carry on under zero, and the ridge you only climb by powers.',
  width: 26,
  height: 18,
  /* On the high road beside the green, with the fields north and the notice
     board one step south. Arriving in the middle of the plains is the answer to
     "where am I" that needs no walking first. */
  start: { x: 11, y: 9 },
  rows: [
    '^^^^^^^^^^^^^^^^^^^^^^^^^^',
    '^^^^^^^^^^^^^^^^^^^^^^^^^^',
    '^^^#^^^^^^^^^^^^^^^^^^^^^^',
    '^^^#^^^^^^^^^^^^^fff,~~~~~',
    'f..#.....#..........,~~~~~',
    'f..#"""""#""".""....,~~~~~',
    'f.f#"""""#""".""....,~~~~~',
    'f..#"""""#"""."".####===#~',
    'f..#..f..#.......#..,~~~~~',
    'f####################~~~~~',
    'f...#.....,,,#......#~~~~~',
    'f%%%#%%%%%...#..f...#...ff',
    'f%%_#___%%.f.#......####ff',
    'f%__####_%...#....f.....ff',
    'f%%%%%%%%%...#........f.ff',
    '^^^^^^^^^^^^^#^^^^^^^^^^^^',
    '^^^^^^^^^^^^^#^^^^^^^^^^^^',
    '^^^^^^^^^^^^^^^^^^^^^^^^^^',
  ],
  /**
   * Eight places, each at the end of its own way out of the village, so
   * arriving somewhere is the end of a road rather than a scramble across a
   * field. Nothing stands in the middle of the high road but the student.
   */
  places: [
    { kind: 'quests', name: 'The Notice Board', emoji: '📜', x: 11, y: 10 },
    { kind: 'road', name: 'The Coast Road', emoji: '🗺️', x: 1, y: 9 },
    { kind: 'region', regionId: 'region-place-value-plains', x: 9, y: 4 },
    { kind: 'region', regionId: 'region-decimal-docks', x: 24, y: 7 },
    { kind: 'region', regionId: 'region-integer-caverns', x: 13, y: 16 },
    { kind: 'region', regionId: 'region-prime-foundry', x: 7, y: 13 },
    { kind: 'region', regionId: 'region-root-and-power-ridge', x: 3, y: 2 },
    { kind: 'boss', bossId: 'boss-number-kingdom', x: 23, y: 12 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Place Value Plains                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Flat country fenced into columns, and one road with two cairns on it.
 *
 * The three fields are five, four and three wide, west to east: a fence cannot
 * draw a ten times, but it can say which column is the bigger one, and that is
 * the whole reason a digit means what its column says it means. Powers of ten
 * is out in the widest field and place value in the middle one. Rounding stands
 * off the road between two boundary cairns and plainly nearer one of them.
 * Order of operations is at the top of the eastern boundary lane, which is not
 * reached by walking in one direction — you leave the road, take a lane, cross
 * the fields and turn, and doing those in another order puts you in another
 * field.
 */
const PLACE_VALUE_PLAINS: RegionMap = {
  kingdomId: NUMBER,
  regionId: 'region-place-value-plains',
  caption:
    'Three fields, fenced in columns, each one west of the last the bigger field — and a road with two cairns on it to be nearer one of.',
  width: 18,
  height: 12,
  /* Out on the plains road with the fields in sight, a few strides in from the
     track back to the village. */
  start: { x: 5, y: 9 },
  rows: [
    'ffffffffffffffffff',
    'f..f........#....f',
    'f......#....#..f.f',
    'f."""""#""""#....f',
    'f."""""#""""#""".f',
    'f."""""#""""#""".f',
    'f."""""#""""#""".f',
    'f."""""#""""#""".f',
    'f......#....#....f',
    'f################f',
    'f.f..^.....^...f.f',
    'ffffffffffffffffff',
  ],
  places: [
    { kind: 'road', name: 'The Plains Track', emoji: '🔢', x: 1, y: 9 },
    { kind: 'concept', conceptId: 'powers-of-ten', x: 4, y: 5 },
    { kind: 'concept', conceptId: 'place-value', x: 9, y: 5 },
    { kind: 'concept', conceptId: 'rounding', x: 9, y: 10 },
    { kind: 'concept', conceptId: 'order-of-operations', x: 12, y: 1 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Decimal Docks                                                              */
/* -------------------------------------------------------------------------- */

/**
 * A harbour wall with the town on one side of it and the water on the other.
 *
 * The quay runs the whole width of the map and there is nothing standing on it,
 * because it is the line rather than a place: everything dry is north of it and
 * everything out over the water is reached by walking south off it. That is
 * what the point does, and it is the only thing this map claims. The three
 * warehouses along the quayside are the dry side; the five jetties are the wet
 * one, and they run out to different depths because a harbour's do.
 */
const DECIMAL_DOCKS: RegionMap = {
  kingdomId: NUMBER,
  regionId: 'region-decimal-docks',
  caption: 'The quay runs the width of the harbour: warehouses on the dry side of it, jetties out over the water on the other.',
  width: 20,
  height: 13,
  /* On the quay itself, between the warehouse doors and the jetty heads, which
     is the one spot on this map that can see both sides of the line. */
  start: { x: 9, y: 5 },
  rows: [
    '%%%%%%%#%%%%%%%%%%%%',
    '%%%%%%...%%%%%..%%%%',
    '%%___%...%___%..%_%%',
    '%%%_%%...%%_%%..%_%%',
    '%..................%',
    '####################',
    '~~=~~~=~~~=~~~=~~~=~',
    '~~=~~~=~~~=~~~=~~~=~',
    '~~=~~~=~~~=~~~=~~~=~',
    '~~=~~~=~~~~~~~=~~~=~',
    '~~~~~~=~~~~~~~=~~~~~',
    '~~~~~~=~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~',
  ],
  places: [
    { kind: 'road', name: 'The Harbour Gate', emoji: '🔢', x: 7, y: 0 },
    { kind: 'concept', conceptId: 'decimal-place-value', x: 3, y: 3 },
    { kind: 'concept', conceptId: 'round-decimals', x: 11, y: 3 },
    { kind: 'concept', conceptId: 'fractions-to-decimals', x: 17, y: 3 },
    { kind: 'concept', conceptId: 'compare-decimals', x: 2, y: 9 },
    { kind: 'concept', conceptId: 'add-decimals', x: 6, y: 11 },
    { kind: 'concept', conceptId: 'multiply-decimals', x: 10, y: 8 },
    { kind: 'concept', conceptId: 'divide-decimals', x: 14, y: 10 },
    { kind: 'concept', conceptId: 'decimal-operations', x: 18, y: 9 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Integer Caverns                                                            */
/* -------------------------------------------------------------------------- */

/**
 * A shaft with two galleries off it, mirrored, and two deeper halls under them.
 *
 * The shaft is where a student lands and it is the only thing on this map that
 * is not doubled: the west gallery and the east gallery are the same chamber
 * the same distance away in opposite directions, tile for tile, which is what
 * opposites and distance from zero are before anybody writes either down.
 * Below them the halls get *wider* rather than deeper — the rationals are five
 * tiles across and the reals eleven, in the same cavern, one under the other.
 */
const INTEGER_CAVERNS: RegionMap = {
  kingdomId: NUMBER,
  regionId: 'region-integer-caverns',
  caption: 'A shaft down through the rock, two galleries that mirror each other exactly, and under them the halls that keep getting wider.',
  width: 17,
  height: 13,
  /* At the foot of the shaft where the galleries part, which is the one tile on
     this map that both of them are the same distance from. */
  start: { x: 8, y: 6 },
  rows: [
    '^^^^^^^^#^^^^^^^^',
    '^^^^^^^^#^^^^^^^^',
    '^^^^^^^^#^^^^^^^^',
    '^^^^^^^^#^^^^^^^^',
    '^^^^^^^^#^^^^^^^^',
    '^^^^^^^^#^^^^^^^^',
    '^^^###########^^^',
    '^____^^^#^^^____^',
    '^____^^^#^^^____^',
    '^^^^^^_____^^^^^^',
    '^^^^^^^^#^^^^^^^^',
    '^^~___________~^^',
    '^^^^^^^^^^^^^^^^^',
  ],
  places: [
    { kind: 'road', name: 'The Cavern Mouth', emoji: '🔢', x: 8, y: 0 },
    { kind: 'concept', conceptId: 'integers', x: 2, y: 8 },
    { kind: 'concept', conceptId: 'absolute-value-opposites', x: 14, y: 8 },
    { kind: 'concept', conceptId: 'rational-numbers', x: 7, y: 9 },
    { kind: 'concept', conceptId: 'real-numbers', x: 4, y: 11 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Prime Foundry                                                              */
/* -------------------------------------------------------------------------- */

/**
 * A works with four moulds in it, and there are only four.
 *
 * The casting floor holds moulds two, three, five and seven tiles long, stacked
 * one above the next, and nothing on this map is four or six long. That is the
 * whole picture: what comes out of this foundry was cast in those and no
 * others. Prime factorisation stands at the head of the bank facing all four;
 * factors and multiples on the sorting floor below them, where the stock is
 * stacked; and through the doorway the furnace hall, where a number is melted
 * down to find out which it was, and the trough two orders draw from at once.
 */
const PRIME_FOUNDRY: RegionMap = {
  kingdomId: NUMBER,
  regionId: 'region-prime-foundry',
  caption: 'Four moulds on the casting floor — two, three, five and seven long — and nothing in the works that is four or six.',
  width: 18,
  height: 13,
  /* On the casting floor at the end of the longest mould, facing the bank. */
  start: { x: 9, y: 9 },
  rows: [
    '%%%%%%%%%%%%%%%%%%',
    '%__________%_____%',
    '%_%%_______%_%%%_%',
    '%__________%_%%%_%',
    '%_%%%______%_%%%_%',
    '%__________%_____%',
    '%_%%%%%__________%',
    '%__________%_____%',
    '%_%%%%%%%__%_____%',
    '%__________%_%%%_%',
    '%______%%%_%_____%',
    '%__________%_____%',
    '%%%%%%#%%%%%%%%%%%',
  ],
  places: [
    { kind: 'road', name: 'The Works Gate', emoji: '🔢', x: 6, y: 12 },
    { kind: 'concept', conceptId: 'factors-multiples', x: 2, y: 10 },
    { kind: 'concept', conceptId: 'prime-factorization', x: 9, y: 5 },
    { kind: 'concept', conceptId: 'prime-composite', x: 14, y: 5 },
    { kind: 'concept', conceptId: 'gcf-lcm', x: 14, y: 11 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Root and Power Ridge                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Three switchbacks up a cliff, and then the ground opens out.
 *
 * Everything below the summit is one path a tile wide, with a ledge off it for
 * each of square roots, cube roots, radicals, rational exponents and scientific
 * notation, in that order going up — climb it by powers, come back down it by
 * roots, and either way there is only the one line to walk. Then the path
 * arrives on the plateau and there is suddenly a second direction: the ring at
 * the top is walked *round* rather than along, with the complex numbers at its
 * centre and the plane they turn in out on the circle. The ridge stops being a
 * line exactly where the numbers do.
 */
const ROOT_AND_POWER_RIDGE: RegionMap = {
  kingdomId: NUMBER,
  regionId: 'region-root-and-power-ridge',
  caption: 'One path a tile wide, switchbacking up the cliff — until the top, where the ground opens out and there is a way round as well as a way along.',
  width: 20,
  height: 15,
  /* At the foot of the climb, a few strides along from the path back down. */
  start: { x: 5, y: 13 },
  rows: [
    '^^^^^^^^^^^^^^^^^^^^',
    '^^......#####.....^^',
    '^^..f...#...#..f..^^',
    '^^......#####.....^^',
    '^^^#^^^^^^^^^^^^^^^^',
    '^^^#^^^#^^^^^^^^^^^^',
    '^^^##############^^^',
    '^^^^^^^^^^^^^#^^#^^^',
    '^^^^^^#^^^^^^^^^#^^^',
    '^^^##############^^^',
    '^^^#^^^^^^^^^^#^^^^^',
    '^^^#^#^^^^^^^^^^^^^^',
    '^^###############^^^',
    '^.........f.....f..^',
    'ffffffffffffffffffff',
  ],
  places: [
    { kind: 'road', name: 'The Ridge Path', emoji: '🔢', x: 1, y: 13 },
    { kind: 'concept', conceptId: 'square-roots', x: 5, y: 11 },
    { kind: 'concept', conceptId: 'cube-roots', x: 6, y: 8 },
    { kind: 'concept', conceptId: 'radicals', x: 14, y: 10 },
    { kind: 'concept', conceptId: 'rational-exponents', x: 13, y: 7 },
    { kind: 'concept', conceptId: 'scientific-notation', x: 7, y: 5 },
    { kind: 'concept', conceptId: 'complex-numbers', x: 10, y: 2 },
    { kind: 'concept', conceptId: 'complex-polar', x: 12, y: 2 },
  ],
};

/** Number Kingdom's village. Registered in `towns.ts`. */
export const NUMBER_TOWN: Town = NUMBER_VILLAGE;

/** All five of Number Kingdom's regions. Registered in `region-maps.ts`. */
export const NUMBER_REGION_MAPS: readonly RegionMap[] = [
  PLACE_VALUE_PLAINS,
  DECIMAL_DOCKS,
  INTEGER_CAVERNS,
  PRIME_FOUNDRY,
  ROOT_AND_POWER_RIDGE,
];
